import { Avatar, Box, Button, Divider, Flex, Grid, Input, Modal, Select, Tooltip } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { IconFileText, IconListSearch, IconSearch } from "@tabler/icons";
import React, { useId } from "react";
import { IMaskInput } from "react-imask";
import { useSelector } from "react-redux";
import useSwr from "swr";
import { ObservationApi, ReminderApi } from "../../apis";
import noImage from "../../assets/no-image.png";
import { IAuth } from "../../interfaces/IAuth";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReminder } from "../../interfaces/IReminder";
import { IReference } from "../../models/General";
import { dateSecFormat } from "../../utils/date";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { renderName } from "../../utils/render-name";
import { ColumnType, Table } from "../table";
import RenderText from "../ui/render-text";
import { CollectionPaymentForm } from "./form";

export function CollectionPaymentReminderList() {
  const [queryStaff, setQueryStaff] = useDebouncedState("", 600);
  const [action, setAction] = React.useState<string[]>([]);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const [filters, setFilters] = React.useState<any>({ listType: "OTHER", programId: "", profileName: "", regNumber: "" });
  const { reminderTypes, reminderResults, reminderLevels, reminderResponses, reminderConnectTypes, repaymentOverDueStatus } = useSelector(
    (state: { general: IGeneral }) => state.general,
  );
  const [programQuery] = React.useState("");
  const id = useId();

  const columns = useHeader({
    reminderResponses,
    reminderResults,
    reminderTypes,
    reminderConnectTypes,
    repaymentOverDueStatus,
    reminderLevels,
    onClick: (key, item) => {
      switch (key) {
        case "condition": {
          setAction(["condition", item as any]);
          break;
        }
      }
    },
  });

  const { data: programData } = useSwr(
    `/collection/observation/program_select?${programQuery}&limit`,
    async () => {
      if ((user?.permissions || []).filter((c) => c.module === "COLLECTION" && c.code === "COLLECTION_OBSERVATION").length > 0) {
        try {
          let res = await ObservationApi.program({ query: programQuery });
          return res;
        } catch (err) {
          Message.error((err as HttpHandler)?.message!);
        }
      }
      return [];
    },
    {},
  );

  const onChangeReg = (e: any) => {
    if (e.length === 7 || e.length === 0) {
      setFilters({ ...filters, regNumber: e });
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ ...filters, profileName: queryStaff });
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [400, queryStaff]);

  const onCancel = async () => {
    setAction([]);
  };

  return (
    <>
      <Grid mb="sm">
        {(user?.permissions || []).filter((c) => c.module === "COLLECTION" && c.code === "COLLECTION_OBSERVATION").length > 0 && (
          <Grid.Col span={4}>
            <Input.Wrapper label="Хөтөлбөрийн нэрээр шүүх">
              <Select
                value={filters.query}
                onChange={(e) => {
                  setFilters({ ...filters, query: e });
                }}
                clearable
                searchable
                placeholder="Сонгох"
                data={(programData || [])?.map((c: any) => ({
                  label: c.name,
                  value: c.id,
                }))}
              />
            </Input.Wrapper>
          </Grid.Col>
        )}
        <Grid.Col span={4}>
          <Input.Wrapper label="Харилцагчийн нэрээр хайх">
            <Input
              onChange={(e) => setQueryStaff(e.currentTarget.value)}
              placeholder="Хайлт хийх"
              rightSection={<IconSearch size={18} style={{ display: "block", opacity: 0.5 }} />}
            />
          </Input.Wrapper>
        </Grid.Col>
        <Grid.Col span={4}>
          <Input.Wrapper id={id} label="Харилцагчийн ТТД-аар хайх" onChange={(e: any) => onChangeReg(e.target.value)}>
            <Input<any> component={IMaskInput} mask="0000000" id={id} placeholder="ТТД - дугаараар хайх" rightSection={<IconListSearch stroke={0.8} />} />
          </Input.Wrapper>
        </Grid.Col>
      </Grid>
      <Divider mb="sm" />
      <Table name="collection-overdue-reminder-other.list" columns={columns} filters={filters} loadData={(data) => ReminderApi.list(data!)} />
      <Modal withCloseButton={false} opened={action[0] === "condition"} onClose={() => setAction([])} centered size="60%">
        <CollectionPaymentForm action={action} onCancel={onCancel} />
      </Modal>
    </>
  );
}

type HeaderProps = {
  onClick: (key: string, record: IReminder) => void;
  reminderResponses: any;
  reminderResults: any;
  reminderTypes: any;
  reminderConnectTypes: any;
  repaymentOverDueStatus: any;
  reminderLevels: any;
};

const useHeader = ({
  onClick,
  reminderResponses,
  reminderResults,
  reminderTypes,
  repaymentOverDueStatus,
  reminderConnectTypes,
  reminderLevels,
}: HeaderProps): ColumnType<IReminder>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Tooltip label="Нэхэмжлэх" position="bottom">
          <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("condition", record)}>
            <IconFileText color="#7956EF" />
          </Button>
        </Tooltip>
      );
    },
  },
  {
    title: "Сануулгын огноо, цаг",
    sorter: true,
    dataIndex: "createdAt",
    render: (record) => {
      return <RenderText text={dateSecFormat(record.createdAt ?? "-")} />;
    },
  },
  {
    title: "Санхүү ажилтан",
    sorter: true,
    dataIndex: "staff",
    render: (record) => {
      return (
        <Flex align="center" gap="xs">
          <Avatar src={record?.financeStaff?.avatar !== null ? record?.financeStaff?.avatar : noImage} size={48} />
          <RenderText text={renderName(record?.financeStaff)} />
        </Flex>
      );
    },
  },

  {
    title: "ТТД",
    sorter: true,
    dataIndex: "regNumber",
    render: (record) => {
      return <RenderText text={record?.repayment?.payerBusiness?.regNumber || "-"} />;
    },
  },
  {
    title: "Партнер",
    sorter: true,
    dataIndex: "businessName",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.repayment?.payerBusiness?.partner?.refCode || "-"} color="#7956EF" />
          <RenderText text={record?.repayment?.payerBusiness?.partner?.businessName || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Бизнес",
    sorter: true,
    dataIndex: "profileName",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.repayment?.payerBusiness?.refCode || "-"} color="#7956EF" />
          <RenderText text={record?.repayment?.payerBusiness?.profileName || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Хугацаа хэтрэлт",
    sorter: true,
    dataIndex: "overDueStatus",
    render: (record) => {
      return <RenderText text={repaymentOverDueStatus.find((c: any) => c.code === record?.repayment?.overDueStatus)?.name ?? "-"} />;
    },
  },
  {
    title: "Эргэн төлөлт нэхэмжлэх",
    sorter: true,
    dataIndex: "repayment",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.repayment?.refCode ?? "-"} color="#7956EF" />
          <RenderText text={dateSecFormat(record?.repayment?.createdAt)} />
        </Box>
      );
    },
  },
  {
    title: "Холболт төрөл",
    sorter: true,
    dataIndex: "connectType",
    render: (record) => {
      return <RenderText text={reminderConnectTypes.find((item2: IReference) => item2?.code === record?.connectType)?.name ?? "-"} />;
    },
  },

  {
    title: "Сануулгын төрөл",
    sorter: true,
    dataIndex: "reminderType",
    render: (record) => {
      return <RenderText text={reminderTypes.find((item2: IReference) => item2?.code === record?.reminderType)?.name ?? "-"} />;
    },
  },
  {
    title: "Сануулгын зэрэглэл",
    sorter: true,
    dataIndex: "reminderLevel",
    render: (record) => {
      return <RenderText text={reminderLevels.find((item2: IReference) => item2?.code === record?.reminderLevel)?.name || "-"} />;
    },
  },
  {
    title: "Холбогдсон үр дүн",
    sorter: true,
    dataIndex: "result",
    render: (record) => {
      return <RenderText text={reminderResults.find((item2: IReference) => item2?.code === record?.result)?.name ?? "-"} />;
    },
  },
  {
    title: "Амлалтын хариу",
    sorter: true,
    dataIndex: "response",
    render: (record) => {
      return <RenderText text={reminderResponses.find((c: any) => c.code === record.response)?.name ?? "-"} />;
    },
  },
];
